<template>
  <section class="admin-content" id="contact-search">
    <Navbar />

    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap"
            style="padding: 32px 0; gap: 16px;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="mdi mdi-receipt" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Transaction Receipt</h4>
                <small style="opacity: .8;">
                  Receipt Date: {{ transaction.created_at | moment("D/M/YYYY") }}
                </small>
              </div>
            </div>

            <div class="text-right">
              <p class="mb-1" style="opacity:.8; font-size:.78rem; text-transform:uppercase; letter-spacing:.05em;">
                Total Amount
              </p>
              <h3 class="mb-0 font-weight-bold">
                <i class="mdi mdi-currency-ngn"></i>{{ transaction.amount | numeral(0, 0) }}
              </h3>
            </div>
          </div>

          <div class="pb-4">
            <button class="btn btn-outline-light btn-sm" @click="printMe">
              <i class="mdi mdi-printer mr-1"></i> Print
            </button>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <div id="printDiv">
          <div class="card table-card shadow mb-4">
            <div class="card-body p-4">
              <div class="row mb-4">
                <div class="col-md-6">
                  <p class="info-label mb-1">To</p>
                  <p class="h5 font-weight-bold mb-2">{{ agency.agency_name }}</p>
                  <span :class="`badge badge-soft-${transaction.color}`">{{ transaction.status }}</span>
                </div>
                <div class="col-md-6 text-md-right">
                  <p class="mb-0 text-muted" style="font-size:.85rem;">
                    Type: <strong class="text-dark">{{ transaction.type }}</strong>
                  </p>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table align-td-middle mb-0">
                  <thead class="table-head">
                    <tr>
                      <th class="table-th">Transaction Details</th>
                      <th class="table-th text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="table-row">
                      <td class="py-2 font-weight-bold">Enrollee(s)</td>
                      <td class="py-2 text-right">
                        <span v-if="transaction.client != null">
                          {{ transaction.client.full_name }} ({{ transaction.client.id_card_number }})
                        </span>
                        <span v-if="transaction.users && transaction.users.length > 0">
                          {{ transaction.users.length }} user(s)
                        </span>
                      </td>
                    </tr>

                    <tr class="table-row">
                      <td class="py-2 font-weight-bold">Ref</td>
                      <td class="py-2 text-right">{{ transaction.transaction_ref }}</td>
                    </tr>

                    <tr class="table-row">
                      <td class="py-2 font-weight-bold">Amount</td>
                      <td class="py-2 text-right font-weight-bold text-primary">
                        <i class="mdi mdi-currency-ngn"></i>{{ transaction.amount | numeral(0, 0) }}
                      </td>
                    </tr>

                    <tr class="table-row">
                      <td class="py-2 font-weight-bold">Narration</td>
                      <td class="py-2 text-right">{{ transaction.description }}</td>
                    </tr>

                    <tr class="table-row">
                      <td class="py-2 font-weight-bold">Date</td>
                      <td class="py-2 text-right">{{ transaction.created_at }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="pt-4 mt-2 border-top text-center">
                <p class="text-muted mb-1" style="font-size:.82rem;">Powered by {{ agency.agency_name }}</p>
                <p class="text-muted mb-0" style="font-size:.78rem; opacity:.75;">&copy; 2026</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Enrollees ── -->
        <div class="card table-card shadow mb-4" v-if="transaction.users && transaction.users.length">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-users text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Enrollees</h6>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Name</th>
                  <th class="table-th">ID</th>
                  <th class="table-th">Contact</th>
                  <th class="table-th">Organization</th>
                  <th class="table-th">Plan</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="enrollee in transaction.users" v-bind:key="enrollee.id">
                  <td class="py-2">
                    <router-link :to="{ path: '/client/' + enrollee.id }">{{ enrollee.full_name }}</router-link>
                  </td>
                  <td class="py-2"><span class="code-chip">{{ enrollee.id_card_number }}</span></td>
                  <td class="py-2">{{ enrollee.phone_number }}</td>
                  <td class="py-2">{{ enrollee.place_of_work }}</td>
                  <td class="py-2">{{ enrollee.sector }}</td>
                  <td class="py-2">
                    <router-link :to="{ path: '/client/' + enrollee.id }">
                      <button type="button" class="btn btn-sm btn-outline-primary">
                        <i class="fe fe-eye"></i>
                      </button>
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Comments ── -->
        <div class="card table-card shadow mb-4">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-message-circle text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Comments</h6>
          </div>
          <div class="card-body pt-0">
            <div v-if="transaction.comments && transaction.comments.length">
              <div class="media mb-3 p-3" style="background:#f8f9fa;border-left:3px solid #2563eb;border-radius:6px;"
                v-for="comment in transaction.comments" v-bind:key="comment.id">
                <div class="media-body">
                  <p class="mb-1">{{ comment.body }}</p>
                  <small class="text-muted" v-if="comment.user != null">
                    <strong>{{ comment.user.full_name }}</strong> &middot;
                    {{ comment.created_at | moment("dddd, MMMM Do YYYY, h:mm:ss a") }}
                  </small>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fe fe-message-circle" style="font-size:2rem;"></i>
              <p class="mt-2 mb-0">No comments yet</p>
            </div>
          </div>
        </div>

      </div>
    </main>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  // import LetterForPayment from "@/views/funds/LetterForPayment.vue";
  // Import component
  import Loading from "vue-loading-overlay";
  // Import stylesheet
  import "vue-loading-overlay/dist/vue-loading.css";
  // Init plugin
  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: {
      Navbar,
      Loading,
    },
    data() {
      return {
        user: null,
        agency: agency,
        transaction: "",
        agencies: "",
        edit: false,
        isLoading: false,
        fullPage: true,
        agency_id: "",
      };
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    methods: {
      printMe() {
        var printContents = document.getElementById("printDiv").innerHTML;
        var originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
      },

      clearIt() {
        this.agency_id = "";
      },

      getPayment() {
        this.axios
          .get(`/api/v1/auth/transactions/${this.$route.params.id}`)
          .then((response) => {
            this.transaction = response.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
    },
    created() {
      this.getPayment();
    },
  };
</script>
